/** "Your feedback" on every admin page: a rating, a topic and a message to the mrigl team. */
import { unwrap, type Schemas } from '@mrigl/api-client';
import { Alert, Button, SelectField, TextAreaField } from '@mrigl/ui';
import { MessageSquareHeart, Star, X } from 'lucide-react';
import { useEffect, useId, useRef, useState, type KeyboardEvent, type SubmitEvent } from 'react';
import { useLocation, useMatch } from 'react-router';
import { api, errorMessage } from './api.js';
import { useI18n } from './i18n.js';

type Category = Schemas['FeedbackSubmission']['category'];
const CATEGORIES: readonly Category[] = ['suggestion', 'problem', 'other'];

export function FeedbackButton() {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const button = useRef<HTMLButtonElement>(null);
  return (
    <>
      <button
        ref={button}
        type="button"
        className="mrigl-button mrigl-button--secondary admin-feedback-button"
        aria-haspopup="dialog"
        onClick={() => {
          setOpen(true);
        }}
      >
        <MessageSquareHeart size={16} aria-hidden="true" />
        <span className="admin-feedback-button__label">{t('admin.feedback.button')}</span>
      </button>
      {open ? (
        <FeedbackDialog
          onClose={() => {
            setOpen(false);
            button.current?.focus();
          }}
        />
      ) : null}
    </>
  );
}

function FeedbackDialog({ onClose }: { readonly onClose: () => void }) {
  const { t, locale } = useI18n();
  const location = useLocation();
  const storeMatch = useMatch('/stores/:storeId/*');
  const storeId = storeMatch?.params.storeId;
  const titleId = useId();
  const panel = useRef<HTMLDivElement>(null);
  const [rating, setRating] = useState<number | null>(null);
  const [category, setCategory] = useState<Category>('suggestion');
  const [message, setMessage] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    panel.current?.querySelector<HTMLElement>('input, select, textarea, button')?.focus();
  }, [sent]);

  async function submit(event: SubmitEvent) {
    event.preventDefault();
    if (rating === null) return;
    setBusy(true);
    setError(null);
    try {
      await unwrap(
        api.POST('/admin-api/v1/me/feedback', {
          body: {
            rating,
            category,
            message: message.trim(),
            page: location.pathname,
            locale,
            storeId: storeId === undefined || storeId === 'new' ? null : storeId,
          },
        }),
      );
      setSent(true);
    } catch (failure) {
      setError(errorMessage(t, failure));
    } finally {
      setBusy(false);
    }
  }

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Escape') {
      event.stopPropagation();
      onClose();
    }
  }

  return (
    <div className="admin-modal" onKeyDown={onKeyDown}>
      <button
        type="button"
        className="admin-modal__scrim"
        aria-label={t('admin.feedback.close')}
        tabIndex={-1}
        onClick={onClose}
      />
      <div
        ref={panel}
        className="admin-modal__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div className="admin-modal__head">
          <h2 id={titleId} className="mrigl-subheading">
            {t('admin.feedback.title')}
          </h2>
          <button
            type="button"
            className="admin-iconbutton"
            aria-label={t('admin.feedback.close')}
            onClick={onClose}
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>
        {sent ? (
          <div className="mrigl-stack">
            <Alert tone="success">{t('admin.feedback.sent')}</Alert>
            <Button onClick={onClose}>{t('admin.feedback.close')}</Button>
          </div>
        ) : (
          <form className="mrigl-stack" onSubmit={(event) => void submit(event)}>
            <p className="mrigl-muted">{t('admin.feedback.intro')}</p>
            {error === null ? null : <Alert tone="error">{error}</Alert>}
            <fieldset className="admin-rating">
              <legend>{t('admin.feedback.rating')}</legend>
              <div className="admin-rating__stars">
                {[1, 2, 3, 4, 5].map((value) => (
                  <label
                    key={value}
                    className={
                      rating !== null && value <= rating ? 'admin-rating__star--on' : undefined
                    }
                  >
                    <input
                      type="radio"
                      name="feedback-rating"
                      value={value}
                      required
                      checked={rating === value}
                      onChange={() => {
                        setRating(value);
                      }}
                    />
                    <Star size={26} aria-hidden="true" />
                    <span className="mrigl-visually-hidden">
                      {t('admin.feedback.star', { count: value })}
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>
            <SelectField
              label={t('admin.feedback.category')}
              value={category}
              onChange={(event) => {
                setCategory(event.target.value as Category);
              }}
              options={CATEGORIES.map((value) => ({
                value,
                label: t(`admin.feedback.category.${value}`),
              }))}
            />
            <TextAreaField
              label={t('admin.feedback.message')}
              value={message}
              required
              maxLength={2000}
              rows={5}
              onChange={(event) => {
                setMessage(event.target.value);
              }}
            />
            <Button type="submit" busy={busy}>
              {t('admin.feedback.send')}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
